import type { AnnotationMediaResultBinding } from "@carbon6/c6-frontend-design/annotation";

/**
 * 트래킹 결과 재생 transport. base mp4 `<video>` 를 명령으로 제어한다. 도착한 트래킹 프레임과
 * 사용자 조작이 같은 재생 위치를 두고 충돌하지 않게 조정한다.
 *
 * 조정의 기준은 **프론티어**다. 트래킹은 앞에서부터 진행하므로 contour 샘플이 있는 구간은 항상
 * `[0, frontier]` 다. 그 밖은 재생해도 그릴 것이 없다. 프론티어를 `seekableRange` 로 공표해
 * 재생 바가 이동할 수 있는 범위를 좁힌다. 재생은 프론티어에서 기다렸다가 프론티어가 늘어나면
 * 이어서 진행한다.
 *
 * 상태는 세 가지다:
 * - `frontierSeconds`: 도착한 샘플의 최대 시각. 단조 증가.
 * - `streamActive`: 프레임이 더 올 수 있는지. `sealFrontier()` 가 한 번 false 로 바꾼다.
 * - `playheadOwner`: 재생 위치를 누가 움직이는지. 사용자 조작이 한 방향으로 소유권을 옮긴다.
 *
 * **`snapshot.playing` 은 "지금 그림이 움직이는가" 가 아니라 "재생을 원하는가" 다.** 프론티어에서
 * 기다리는 동안 element 는 멈춰 있지만 `playing` 은 true 로 남는다. 그래야 프론티어가 늘어났을
 * 때 이어서 재생할 수 있고, 재생 바도 정지가 아니라 재생 중으로 보인다. 그래서 DOM `pause`
 * 이벤트는 이 값을 바꾸지 않는다. 사용자가 직접 누른 `pause()` 만 이 값을 false 로 바꾼다. 이것이
 * 대기와 정지를 구분하는 유일한 근거다. c6 는 `registerVideo` 가 있으면 element 를 스냅샷에
 * 맞춰 제어하지 않는다(`MediaResultPlayer`). 그래서 이 값이 element 와 달라도 충돌하지 않는다.
 *
 * Owns: 재생 위치 조정 · 프론티어 · `seekableRange` 공표 · seek 확정 게이트.
 * Does not own: 샘플 저장(`AnnotationTimedContourSource`) · 스트림 수명(컨트롤러).
 */

export type TrackingTransport = AnnotationMediaResultBinding["transport"] & {
  registerVideo(channelId: string): (element: HTMLVideoElement | null) => void;
  /** 도착한 트래킹 프레임의 시각. 프론티어를 늘리고, 스트림이 소유자일 때만 화면도 옮긴다. */
  advanceToSeconds(seconds: number): void;
  /** 더 올 프레임이 없다. 프론티어에 있으면 재생을 멈춘다. */
  sealFrontier(): void;
  dispose(): void;
};

export type TrackingPlaybackSnapshot = ReturnType<
  AnnotationMediaResultBinding["transport"]["getSnapshot"]
>;

type PlayheadOwner = "stream" | "user";

const VIDEO_HAVE_METADATA = 1;
/** 목표가 이보다 가까우면 이미 그 위치로 본다. 같은 위치로 다시 seek 해 왕복을 낭비하지 않는다. */
const SEEK_SETTLED_SECONDS = 0.001;

export class HtmlVideoTrackingTransport implements TrackingTransport {
  private readonly listeners = new Set<() => void>();
  private video: HTMLVideoElement | null = null;
  private snapshot: TrackingPlaybackSnapshot;
  // 아직 그림으로 확인되지 않은 seek 목표. 원격 mp4 의 seek 은 왕복이다. 그 사이 도착한 프레임은
  // 쌓지 않고 마지막 목표만 남긴다(합치기). seeked 가 확인한다.
  private pendingSeekSeconds: number | null = null;
  private seekInFlight = false;
  // 비디오가 끝까지 그리지 못하는 상황(로드 실패)에서는 게이트를 해제해 오버레이만이라도 진행하게 한다.
  private videoBroken = false;
  private frontierSeconds = 0;
  private streamActive = true;
  // 시작 직후에는 스트림이 화면을 움직인다. 사용자가 아직 명령을 주지 않았다.
  private playheadOwner: PlayheadOwner = "stream";

  constructor(durationSeconds: number) {
    const duration = Number.isFinite(durationSeconds) ? durationSeconds : 0;
    this.snapshot = {
      // 트래킹이 실행되는 동안 결과는 스스로 진행한다. 재생 바가 정지처럼 보이면 안 된다.
      playing: true,
      currentTimeSeconds: 0,
      durationSeconds: duration,
      playbackRate: 1,
      status: "playing",
      capabilities: this.capabilities(),
    };
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): TrackingPlaybackSnapshot => this.snapshot;

  registerVideo =
    (_channelId: string) =>
    (video: HTMLVideoElement | null): void => {
      if (video === this.video) return;
      // 결과가 화면에서 제거되었다가 돌아오면 element 는 새로 생긴다(c6 가 결과 플레이어를
      // 언마운트했다가 다시 마운트한다). 새 element 는 0초에서 시작한다. 알고 있던 위치를 목표로
      // 설정하지 않으면 돌아온 결과가 처음부터 다시 재생된다.
      const resumeSeconds = this.snapshot.currentTimeSeconds;
      this.detach();
      this.video = video;
      if (video === null) return;
      for (const [name, listener] of this.videoListeners) {
        video.addEventListener(name, listener);
      }
      this.videoBroken = false;
      this.pendingSeekSeconds = this.clampToSeekable(resumeSeconds);
      this.flushSeek();
      this.syncFromVideo();
      this.syncPlayback();
    };

  play = (): void => {
    this.playheadOwner = "user";
    this.setSnapshot({ playing: true, status: "playing" });
    this.startElement();
  };

  pause = (): void => {
    // 사용자가 직접 멈췄다. 프론티어가 늘어나도 이어서 재생하지 않는다.
    this.playheadOwner = "user";
    this.video?.pause();
    this.setSnapshot({ playing: false, status: "paused" });
  };

  toggle = (): void => {
    if (this.snapshot.playing) this.pause();
    else this.play();
  };

  /** 사용자 의도(재생 바, replay). 재생 중이어도 목표를 즉시 바꾼다. */
  seekToSeconds = (seconds: number): void => {
    // 사용자가 위치를 골랐다. 다음 프레임이 그 위치를 바꾸지 않는다.
    this.playheadOwner = "user";
    this.requestSeek(seconds, "command");
  };

  /**
   * 도착한 트래킹 프레임의 시각. 프론티어를 늘리고, 스트림이 소유자일 때만 화면을 옮긴다.
   * 사용자가 명령을 준 뒤에는 화면을 바꾸지 않는다. 재생이 끝났어도 다음 프레임이 재생 위치를
   * 트래킹 지점으로 되감으면 안 된다.
   */
  advanceToSeconds = (seconds: number): void => {
    this.raiseFrontier(seconds);
    if (this.playheadOwner === "stream") this.requestSeek(seconds, "stream");
    this.syncPlayback();
  };

  sealFrontier = (): void => {
    if (!this.streamActive) return;
    this.streamActive = false;
    this.syncPlayback();
  };

  setPlaybackRate = (rate: number): void => {
    if (!Number.isFinite(rate) || rate <= 0) return;
    if (this.video !== null) this.video.playbackRate = rate;
    this.setSnapshot({ playbackRate: rate });
  };

  reset = (): void => {
    this.pause();
    this.seekToSeconds(0);
  };

  dispose(): void {
    this.detach();
    this.listeners.clear();
  }

  private readonly videoListeners = [
    // 재생 중에는 seeked 를 받지 못해도 timeupdate 가 그림이 진행한다는 증거다. 확인이 오지 않아
    // 오버레이가 멈추는 것을 막는다.
    ["timeupdate", () => this.settleSeek(this.video?.seeking === false)],
    ["durationchange", () => this.syncFromVideo()],
    ["loadedmetadata", () => this.settleSeek(false)],
    ["canplay", () => this.settleSeek(false)],
    ["seeked", () => this.settleSeek(true)],
    ["play", () => this.syncFromVideo()],
    // pause 는 재생 의도를 해제하지 않는다. 프론티어 대기가 element 를 멈추기 때문이다.
    ["pause", () => this.syncFromVideo()],
    ["ended", () => this.setSnapshot({ playing: false, status: "ended" })],
    ["ratechange", () => this.syncFromVideo()],
    ["error", () => this.failVideo()],
  ] as const;

  /** 샘플이 있는 구간은 항상 `[0, frontier]` 다. 이 객체를 만드는 곳은 여기뿐이다. */
  private capabilities(): TrackingPlaybackSnapshot["capabilities"] {
    return {
      live: false,
      seek: true,
      playbackRate: true,
      seekableRange: { startSeconds: 0, endSeconds: this.frontierSeconds },
    };
  }

  private raiseFrontier(seconds: number): void {
    const target = Number.isFinite(seconds) ? seconds : 0;
    const next = Math.min(this.snapshot.durationSeconds, Math.max(0, target));
    if (next <= this.frontierSeconds) return;
    this.frontierSeconds = next;
    // readyState 가 낮으면 video 이벤트가 하나도 오지 않으므로 여기서 직접 공표해야 한다.
    this.setSnapshot({ capabilities: this.capabilities() });
  }

  /**
   * 재생이 추적보다 앞서지 않게 막는 유일한 곳. 프론티어에 닿으면 element 만 멈추고 재생 의도는
   * 남긴다. 프론티어가 늘어나면 여기서 다시 재생한다.
   *
   * 프론티어로 되감지는 않는다. timeupdate 사이에 조금 넘어설 수 있다. 되감으면 원격 mp4 seek
   * 왕복과 눈에 보이는 점프가 생기고, 공표 시각이 그림보다 뒤처진다. 넘어선 부분은 hold 샘플링이
   * 처리한다.
   */
  private syncPlayback(): void {
    const video = this.video;
    if (video === null || this.videoBroken) return;
    if (!this.snapshot.playing) return;
    if (video.ended) {
      this.setSnapshot({ playing: false, status: "ended" });
      return;
    }
    if (video.currentTime < this.frontierSeconds - SEEK_SETTLED_SECONDS) {
      if (this.playheadOwner === "user" && video.paused) this.startElement();
      return;
    }
    if (!video.paused) video.pause();
    // 더 올 프레임이 없으면 기다릴 이유도 없다. 여기가 이 결과의 끝이다.
    if (!this.streamActive) this.setSnapshot({ playing: false, status: "paused" });
  }

  private startElement(): void {
    const result = this.video?.play();
    if (result !== undefined) void result.catch(() => undefined);
  }

  private requestSeek(seconds: number, source: "command" | "stream"): void {
    const next = this.clampToSeekable(seconds);
    const video = this.video;
    if (video === null || this.videoBroken) {
      // 확인할 그림이 없으므로 요청 시각을 그대로 공표한다. 비디오가 연결되면 flushSeek 이 따라간다.
      this.pendingSeekSeconds = next;
      this.setSnapshot({ currentTimeSeconds: next });
      return;
    }
    this.pendingSeekSeconds = next;
    if (source === "command") this.seekInFlight = false;
    this.flushSeek();
  }

  /**
   * 추적된 구간 밖으로는 나가지 않는다. c6 의 모든 seek 경로가 `seekableRange` 를 따른다. 그래서
   * 이 clamp 는 UI 결손을 보완하는 것이 아니라 transport 가 자기 경계를 직접 지키는 것이다.
   * 프론티어는 도착한 샘플이 정하고, 그 판정은 c6 가 아니라 여기에 있다.
   */
  private clampToSeekable(seconds: number): number {
    const target = Number.isFinite(seconds) ? seconds : 0;
    return Math.min(this.frontierSeconds, Math.max(0, target));
  }

  private flushSeek(): void {
    const video = this.video;
    const target = this.pendingSeekSeconds;
    if (video === null || target === null || this.videoBroken) return;
    if (this.seekInFlight) return; // 확인(seeked)이 다음 목표를 처리한다
    if (video.readyState < VIDEO_HAVE_METADATA) return; // loadedmetadata 가 이어서 처리한다
    this.pendingSeekSeconds = null;
    if (Math.abs(video.currentTime - target) < SEEK_SETTLED_SECONDS) {
      this.syncFromVideo();
      return;
    }
    this.seekInFlight = true;
    video.currentTime = target;
  }

  private settleSeek(confirmed: boolean): void {
    if (confirmed) this.seekInFlight = false;
    this.syncFromVideo();
    this.flushSeek();
    this.syncPlayback();
  }

  private failVideo(): void {
    this.videoBroken = true;
    this.seekInFlight = false;
    this.pendingSeekSeconds = null;
    this.syncFromVideo();
  }

  private syncFromVideo(): void {
    const video = this.video;
    if (video === null) return;
    const durationSeconds = Number.isFinite(video.duration)
      ? video.duration
      : this.snapshot.durationSeconds;
    const playing = this.snapshot.playing;
    this.setSnapshot({
      // element 의 paused 를 읽지 않는다. 프론티어 대기가 element 만 멈추기 때문이다.
      playing,
      // seek 이 확인되기 전의 video.currentTime 은 "요청한 위치" 일 뿐 아직 그림이 아니다.
      // 그 값을 공표하면 contour 오버레이만 앞서간다.
      currentTimeSeconds: this.seekInFlight ? this.snapshot.currentTimeSeconds : video.currentTime,
      durationSeconds,
      playbackRate: video.playbackRate,
      status: video.error ? "error" : playing ? "playing" : video.ended ? "ended" : "paused",
      capabilities: this.capabilities(),
    });
  }

  private detach(): void {
    this.pendingSeekSeconds = null;
    this.seekInFlight = false;
    if (this.video !== null) {
      for (const [name, listener] of this.videoListeners) {
        this.video.removeEventListener(name, listener);
      }
    }
    this.video = null;
  }

  private setSnapshot(patch: Partial<TrackingPlaybackSnapshot>): void {
    const next = { ...this.snapshot, ...patch };
    const before = this.snapshot.capabilities;
    const after = next.capabilities;
    if (
      next.playing === this.snapshot.playing &&
      next.currentTimeSeconds === this.snapshot.currentTimeSeconds &&
      next.durationSeconds === this.snapshot.durationSeconds &&
      next.playbackRate === this.snapshot.playbackRate &&
      next.status === this.snapshot.status &&
      // c6 snapshotsEqual 과 같은 기준이어야 구독자에게 알림이 간다.
      after?.live === before?.live &&
      after?.seek === before?.seek &&
      after?.playbackRate === before?.playbackRate &&
      after?.seekableRange?.startSeconds === before?.seekableRange?.startSeconds &&
      after?.seekableRange?.endSeconds === before?.seekableRange?.endSeconds
    ) {
      return;
    }
    this.snapshot = next;
    for (const listener of this.listeners) listener();
  }
}
